/**
 * The Trades list (TRADE-LIST, Pedro 2026-10-25; migrations 0120 / 0121 / 0122): one row per saved
 * trade — serial TR-000001, its status, date and time created, portfolio, (agreement), title,
 * orders and transfers, created by, last saved, its stored PDF / Excel.
 *
 * Used on Portfolios → Trades (every portfolio) and Agreement → Trades (its linked portfolios).
 * TRADE-FLOW (OPEN_ITEMS, T-F1…T-F8): a click on a row opens the trade's side panel (U2), where its
 * timeline, documents and emails are, and its next steps — each committed at once through its own
 * confirm (§3g Pattern B), so neither page stages anything. The status is filtered from its header,
 * not pills (U3). Readable by everyone who can read portfolios; steps by `trades.draft`.
 * Every header sorts and filters (the grids' header popover, C-293).
 */
import { Fragment, useMemo, useState } from 'react';
import { ActionIcon, Anchor, Badge, Group, Popover, Table, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconChevronDown, IconChevronRight, IconFilter, IconSortAscending, IconSortDescending } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';

import { tradesApi, type TradeDraft, type TradeRun, type TradeStatus } from '../api/trades';
import { ColumnMenu, filterActive, passesFilter, type Filters, type Ft, type SortState } from './GridColumnMenu';
import { StatusExplained } from './StatusExplained';
import { RunPanel } from './RunPanel';
import { TradePanel } from './TradePanel';

export const STATUS_COLOR: Record<TradeStatus, string> = {
  draft: 'gray', with_client: 'blue', approved: 'teal', sent: 'indigo', executed: 'green', cancelled: 'red', revised: 'gray',
};

export function TradeStatusBadge({ t }: { t: TradeDraft }) {
  const s = t.status ?? 'draft';
  const label = t.status_label ?? 'Draft';
  return (
    <StatusExplained entity="trade" status={label}>
      <Badge size="sm" color={STATUS_COLOR[s]} variant={s === 'revised' ? 'outline' : 'light'} style={{ textTransform: 'none' }}>{label}</Badge>
    </StatusExplained>
  );
}

/** TF-ROWFLOW (Pedro 2026-10-25): the lifecycle in a row, read-only — five dots filled up to the
 *  step reached, then a red (Cancelled) or grey (Revised) end; hover a dot for who / when. */
export function TradeFlowStrip({ t }: { t: TradeDraft }) {
  const steps: { label: string; at?: string | null; by?: string | null }[] = [
    { label: 'Created', at: t.created_at, by: t.created_by_name },
    { label: 'Emailed to client', at: t.client_pending_at, by: t.client_pending_by_name },
    { label: 'Approved by client', at: t.approved_at, by: t.approved_by_name },
    { label: 'Sent for execution', at: t.sent_at, by: t.sent_by_name },
    { label: 'Executed', at: t.executed_at, by: t.executed_by_name },
  ];
  const s = t.status ?? 'draft';
  const end = s === 'cancelled' ? { label: 'Cancelled', at: t.cancelled_at, by: t.cancelled_by_name, color: 'var(--mantine-color-red-6)' }
    : s === 'revised' ? { label: `Revised${t.revised_as ? ` as ${t.revised_as.serial}` : ''}`, at: t.rejected_at, by: t.rejected_by_name, color: 'var(--mantine-color-gray-6)' }
      : null;
  const reached = (i: number) => i === 0 || !!steps[i].at;
  const dot = (key: string, tip: string, fill: string | null, ring?: string) => (
    <Tooltip key={key} label={tip} withArrow multiline w={240}>
      <span style={{ width: 9, height: 9, borderRadius: '50%', flex: 'none', display: 'inline-block',
        background: fill ?? 'transparent', border: `1.5px solid ${ring ?? fill ?? 'var(--mantine-color-gray-4)'}` }} />
    </Tooltip>
  );
  const line = (key: string, on: boolean) => (
    <span key={key} style={{ width: 10, height: 2, flex: 'none', background: on ? 'var(--mantine-color-azul-6)' : 'var(--mantine-color-gray-3)' }} />
  );
  const out: React.ReactNode[] = [];
  steps.forEach((st, i) => {
    if (i > 0) out.push(line(`l${i}`, reached(i)));
    const tip = reached(i) ? `${st.label}: ${st.by ?? 'someone'}, ${dateOf(st.at)} ${timeOf(st.at)}` : `${st.label}: not yet`;
    out.push(dot(`d${i}`, tip, reached(i) ? 'var(--mantine-color-azul-6)' : null));
  });
  if (end) {
    out.push(line('le', true));
    out.push(dot('end', `${end.label}: ${end.by ?? 'someone'}, ${dateOf(end.at)} ${timeOf(end.at)}`, end.color));
  }
  return <span style={{ display: 'inline-flex', alignItems: 'center' }} aria-label={`Lifecycle: ${t.status_label ?? 'Draft'}`}>{out}</span>;
}

const when = (iso: string | null | undefined) => (iso ? new Date(iso) : null);
export const dateOf = (iso: string | null | undefined) => when(iso)?.toLocaleDateString() ?? '-';
export const timeOf = (iso: string | null | undefined) =>
  when(iso)?.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) ?? '-';
const isoDay = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : '');
/** The latest of some times (ISO strings sort as times), or null. */
const latest = (xs: (string | null | undefined)[]) => xs.filter((x): x is string => !!x).sort().pop() ?? null;

const TH = { whiteSpace: 'normal', lineHeight: 1.2, verticalAlign: 'bottom' } as const;
const NOWRAP = { whiteSpace: 'nowrap' } as const;

/** A sortable / filterable column: what a cell shows (`text`), and its number when it has one. */
interface Col { key: string; label: string; ft: Ft; width?: number; right?: boolean; text: (t: TradeDraft) => string; num?: (t: TradeDraft) => number | null }

export function TradesList({ agreementId, showAgreement, canAct, override, onOpen, onOpenRun }: {
  agreementId?: string;
  showAgreement?: boolean;
  /** `trades.draft`: may take the steps and open a draft. */
  canAct: boolean;
  /** The Admin override (Beta) is on (always false for non-Admins). */
  override: boolean;
  /** Open a draft in trade mode (a Draft; any trade with the Admin override). */
  onOpen: (t: TradeDraft) => void;
  /** TRADE-CONS 4b: open a run in its consolidation's trade mode. */
  onOpenRun: (r: TradeRun) => void;
}) {
  const q = useQuery({ queryKey: ['trades', 'list', agreementId ?? 'all'], queryFn: () => tradesApi.listAll(agreementId) });
  // TRADE-CONS 4b (S4-Q11): a run is one row (its serial, consolidation, status) that opens into
  // its portfolios' drafts; a draft of a run is never listed on its own.
  const runsQ = useQuery({ queryKey: ['trades', 'runs', agreementId ?? ''], queryFn: () => tradesApi.runs.list(agreementId) });
  const runs = useMemo(() => runsQ.data?.items ?? [], [runsQ.data]);
  const [openRuns, setOpenRuns] = useState<Set<string>>(new Set());
  const rows = useMemo(() => {
    const drafts = q.data?.items ?? [];
    const byRun = new Map<string, TradeDraft[]>();
    for (const d of drafts) if (d.run_id) byRun.set(d.run_id, [...(byRun.get(d.run_id) ?? []), d]);
    // A run as a row of the list: its own serial, title and status; its drafts' counts added up.
    const runRows: TradeDraft[] = runs.map((r) => {
      const kids = byRun.get(r.id) ?? [];
      return {
        id: `run:${r.id}`, portfolio: '', title: r.title, unit: r.unit, settle_default: 'ref', settle_default_account: null,
        base_draft_id: null, base_title: null, as_of_date: kids[0]?.as_of_date ?? '', latest_positions_date: null,
        newer_positions: false, problems: [], updated_at: r.updated_at, updated_by_name: r.updated_by_name,
        line_count: kids.reduce((s, d) => s + d.line_count, 0), transfer_count: kids.reduce((s, d) => s + (d.transfer_count ?? 0), 0),
        serial: r.serial, created_at: r.created_at, created_by_name: r.created_by_name, locked: r.locked,
        portfolio_label: `Consolidation ${r.consolidation_name ?? '?'} · ${kids.length} portfolio${kids.length === 1 ? '' : 's'}`,
        agreement_id: r.agreement_id, agreement_label: r.agreement_label, status: r.status, status_label: r.status_label,
        // 4e: the run's steps for its lifecycle dots — Sent once every bank is, Executed once every portfolio is.
        client_pending_at: r.client_pending_at, client_pending_by_name: r.client_pending_by_name,
        approved_at: r.approved_at, approved_by_name: r.approved_by_name,
        sent_at: r.status === 'sent' || r.status === 'executed' ? latest((r.banks ?? []).map((b) => b.sent_at)) : null,
        sent_by_name: (r.banks ?? []).find((b) => b.sent_at)?.sent_by_name ?? null,
        executed_at: r.status === 'executed' ? latest(kids.map((d) => d.executed_at)) : null,
        executed_by_name: kids.find((d) => d.executed_at)?.executed_by_name ?? null,
        rejected_at: r.rejected_at, rejected_by_name: r.rejected_by_name,
        cancelled_at: r.cancelled_at, cancelled_by_name: r.cancelled_by_name, revised_as: r.revised_as,
      };
    });
    return [...runRows, ...drafts.filter((d) => !d.run_id)];
  }, [q.data, runs]);
  const runOf = (id: string) => runs.find((r) => `run:${r.id}` === id);
  const kidsOf = (runId: string) => (q.data?.items ?? []).filter((d) => d.run_id === runId);
  const [filters, setFilters] = useState<Filters>({});
  const [sort, setSort] = useState<SortState | null>(null);
  const [openCol, setOpenCol] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [selectedRun, setSelectedRun] = useState<string | null>(null);

  const cols: Col[] = useMemo(() => [
    { key: 'serial', label: 'Trade', ft: 'text', width: 100, text: (t) => t.serial ?? '', num: (t) => Number((t.serial ?? '').replace(/\D/g, '')) || null },
    { key: 'status', label: 'Status', ft: 'enum', width: 130, text: (t) => t.status_label ?? 'Draft' },
    { key: 'date', label: 'Date created', ft: 'date', width: 96, text: (t) => isoDay(t.created_at) },
    { key: 'time', label: 'Time created', ft: 'text', width: 70, text: (t) => timeOf(t.created_at) },
    { key: 'portfolio', label: 'Portfolio', ft: 'enum', width: 150, text: (t) => t.portfolio_label ?? t.portfolio },
    ...(showAgreement ? [{ key: 'agreement', label: 'Agreement', ft: 'enum' as Ft, width: 130, text: (t: TradeDraft) => t.agreement_label ?? 'Not linked' }] : []),
    { key: 'title', label: 'Title', ft: 'text', text: (t) => t.title ?? 'Untitled' },
    { key: 'orders', label: 'Orders', ft: 'num', width: 64, right: true, text: (t) => String(t.line_count), num: (t) => t.line_count },
    { key: 'transfers', label: 'Transfers', ft: 'num', width: 76, right: true, text: (t) => String(t.transfer_count ?? 0), num: (t) => t.transfer_count ?? 0 },
    { key: 'created_by', label: 'Created by', ft: 'enum', width: 130, text: (t) => t.created_by_name ?? '-' },
    { key: 'saved', label: 'Last saved', ft: 'date', width: 150, text: (t) => isoDay(t.updated_at) },
  ], [showAgreement]);

  const options = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const c of cols) if (c.ft === 'enum') out[c.key] = [...new Set(rows.map((r) => c.text(r)))].sort();
    return out;
  }, [cols, rows]);
  const shown = useMemo(() => {
    const active = cols.filter((c) => filterActive(c, filters[c.key]));
    const out = rows.filter((r) => active.every((c) => passesFilter(c.ft, filters[c.key], c.text(r), c.num ? c.num(r) : null)));
    const sc = sort && cols.find((c) => c.key === sort.key);
    if (sc) {
      const v = (r: TradeDraft): number | string => (sc.num ? (sc.num(r) ?? -Infinity) : sc.key === 'time' ? (r.created_at ?? '') : sc.text(r));
      out.sort((a, b) => {
        const x = v(a), y = v(b);
        const d = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
        return sort.dir === 'desc' ? -d : d;
      });
    }
    return out;
  }, [rows, cols, filters, sort]);

  const current = (q.data?.items ?? []).find((r) => r.id === selected) ?? null;

  if (q.isLoading || runsQ.isLoading) return <Text size="sm" c="dimmed">Loading trades…</Text>;
  if (q.isError || runsQ.isError) return <Text size="sm" c="red">The trades could not be read.</Text>;
  if (!rows.length) return <Text size="sm" c="dimmed">No trades yet{agreementId ? ' for the portfolios of this agreement' : ''}.</Text>;

  const head = (c: Col): React.ReactNode => {
    const active = filterActive(c, filters[c.key]) || sort?.key === c.key;
    return (
      <Table.Th key={c.key} style={{ ...TH, width: c.width, textAlign: c.right ? 'right' : undefined,
        background: active ? 'var(--mantine-color-azul-1)' : undefined }}>
        <Popover width={250} position="bottom-start" withArrow shadow="md" trapFocus keepMounted={false}
          opened={openCol === c.key} onChange={(o) => setOpenCol(o ? c.key : null)}>
          <Popover.Target>
            <Group gap={4} wrap="nowrap" role="button" aria-label={`Sort or filter ${c.label}`} style={{ cursor: 'pointer' }}
              onClick={() => setOpenCol(openCol === c.key ? null : c.key)}>
              <span>{c.label}</span>
              <span style={{ color: active ? 'var(--mantine-color-azul-7)' : 'var(--mantine-color-gray-5)', display: 'inline-flex' }}>
                {sort?.key === c.key ? (sort.dir === 'asc' ? <IconSortAscending size={13} /> : <IconSortDescending size={13} />) : <IconFilter size={13} />}
              </span>
            </Group>
          </Popover.Target>
          <Popover.Dropdown>
            <ColumnMenu col={c} options={options[c.key] ?? []} initialFilter={filters[c.key]} sort={sort}
              onSort={(dir) => { setSort((p) => (p && p.key === c.key && p.dir === dir ? null : { key: c.key, dir })); setOpenCol(null); }}
              onApply={(draft) => { setFilters((p) => ({ ...p, [c.key]: draft })); setOpenCol(null); }}
              onClear={() => { setFilters((p) => { const n = { ...p }; delete n[c.key]; return n; }); setSort((p) => (p?.key === c.key ? null : p)); setOpenCol(null); }} />
          </Popover.Dropdown>
        </Popover>
      </Table.Th>
    );
  };
  const open = (t: TradeDraft, fmt: 'pdf' | 'xlsx') => tradesApi.openStored(t.id, fmt).catch((e) =>
    notifications.show({ color: 'red', title: 'Document not opened', message: e instanceof Error ? e.message : 'Something went wrong.' }));
  const filtered = cols.filter((c) => filterActive(c, filters[c.key])).length;

  return (
    <>
      {(filtered > 0 || sort) && (
        <Group gap="xs" mb={4}>
          {filtered > 0 && <Badge color="azul" variant="light" style={{ textTransform: 'none' }}>Filtered: {shown.length} of {rows.length} trades</Badge>}
          <Anchor size="xs" onClick={() => { setFilters({}); setSort(null); }}>Clear filters and sort</Anchor>
        </Group>
      )}
      <Table striped highlightOnHover withTableBorder fz="sm" style={{ tableLayout: 'auto' }}>
        <Table.Thead>
          <Table.Tr>
            {cols.flatMap((c) => (c.key === 'orders'
              ? [<Table.Th key="flow" style={{ ...TH, width: 110 }}>Lifecycle</Table.Th>, head(c)] : [head(c)]))}
            <Table.Th style={{ ...TH, width: 96 }}>Documents</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {shown.map((t) => {
            const run = runOf(t.id);
            if (!run) return draftRow(t, false);
            const open = openRuns.has(run.id);
            const toggle = () => setOpenRuns((s) => { const x = new Set(s); if (x.has(run.id)) x.delete(run.id); else x.add(run.id); return x; });
            const why = !canAct ? 'Needs the Draft trades permission'
              : run.locked && !override ? `${run.serial} is ${run.status_label}: only a Draft run is opened in trade mode` : null;
            return (
              <Fragment key={t.id}>
                <Table.Tr title={`Run ${run.serial}: a consolidation traded as one. Click to open its panel (history, documents, emails and next step); the arrow ${open ? 'closes' : 'shows'} its portfolios' drafts.`}
                  style={{ cursor: 'pointer', background: run.id === selectedRun ? 'var(--mantine-color-azul-0)' : 'var(--mantine-color-gray-1)' }}
                  onClick={() => { setSelected(null); setSelectedRun(run.id); }}>
                  <Table.Td style={NOWRAP}>
                    <Group gap={4} wrap="nowrap">
                      <ActionIcon size="xs" variant="subtle" color="gray" aria-label={`${open ? 'Close' : 'Show'} the drafts of ${run.serial}`}
                        onClick={(e) => { e.stopPropagation(); toggle(); }}>
                        {open ? <IconChevronDown size={13} /> : <IconChevronRight size={13} />}
                      </ActionIcon>
                      <Text size="sm" fw={700} span>{t.serial}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td style={NOWRAP}><TradeStatusBadge t={t} /></Table.Td>
                  <Table.Td style={NOWRAP}>{dateOf(t.created_at)}</Table.Td>
                  <Table.Td style={NOWRAP}>{timeOf(t.created_at)}</Table.Td>
                  <Table.Td style={NOWRAP}>{t.portfolio_label}{run.consolidation_deleted && <Text span size="xs" c="red"> (consolidation deleted)</Text>}</Table.Td>
                  {showAgreement && <Table.Td style={NOWRAP}>{t.agreement_label ?? '-'}</Table.Td>}
                  <Table.Td>{t.title ?? <Text span size="sm" c="dimmed">Untitled</Text>}</Table.Td>
                  <Table.Td style={NOWRAP}><TradeFlowStrip t={t} /></Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{t.line_count}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{t.transfer_count ?? 0}</Table.Td>
                  <Table.Td style={NOWRAP}>{t.created_by_name ?? '-'}</Table.Td>
                  <Table.Td style={NOWRAP}>{t.updated_by_name ?? '-'} · {dateOf(t.updated_at)}</Table.Td>
                  <Table.Td style={NOWRAP} onClick={(e) => e.stopPropagation()}>
                    <Group gap={8} wrap="nowrap">
                      {/* 4d (0132): the client PDF and each bank's Excel, made when the run was last saved. */}
                      {(run.files ?? []).length ? (
                        <Tooltip withArrow label={`Made when saved: ${dateOf(run.documents_at)} ${timeOf(run.documents_at)}`}>
                          <Group gap={8} wrap="nowrap">
                            {(run.files ?? []).map((f) => (
                              <Anchor key={f.id} size="sm" onClick={() => void tradesApi.runs.openFile(run.id, f).catch((e) =>
                                notifications.show({ color: 'red', title: 'Document not opened', message: e instanceof Error ? e.message : 'Something went wrong.' }))}>
                                {f.kind === 'client_pdf' ? 'PDF' : `Excel ${f.bank ?? ''}`.trim()}
                              </Anchor>
                            ))}
                          </Group>
                        </Tooltip>
                      ) : <Text span size="xs" c="dimmed">Save to make</Text>}
                      <Tooltip withArrow multiline w={260} label={why ?? "Open the run in its consolidation's trade mode (View and trade)"}>
                        <Anchor size="sm" c={why ? 'dimmed' : undefined} onClick={() => { if (!why) onOpenRun(run); }}>Open run</Anchor>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
                {open && kidsOf(run.id).map((d) => draftRow(d, true))}
              </Fragment>
            );
          })}
        </Table.Tbody>
      </Table>
      <TradePanel trade={current} onClose={() => setSelected(null)} canAct={canAct} override={override}
        onSelect={(id) => setSelected(id)} onSelectRun={(id) => { setSelected(null); setSelectedRun(id); }}
        onOpen={(t) => {
          setSelected(null);
          // A run's draft opens through its run (O8).
          const run = t.run_id ? runs.find((r) => r.id === t.run_id) : undefined;
          if (run) onOpenRun(run); else onOpen(t);
        }} />
      {/* 4e: a run's panel — its steps, taken for the whole run (its drafts follow). */}
      <RunPanel run={runs.find((r) => r.id === selectedRun) ?? null} onClose={() => setSelectedRun(null)} canAct={canAct}
        onOpenRun={(r) => { setSelectedRun(null); onOpenRun(r); }}
        onSelectRun={(id) => setSelectedRun(id)}
        onSelectDraft={(id) => { setSelectedRun(null); setSelected(id); }} />
    </>
  );

  function draftRow(t: TradeDraft, inRun: boolean) {
    return (
            <Table.Tr key={t.id} title="Open the trade's panel: its history, documents, emails and next step"
              style={{ cursor: 'pointer', background: t.id === selected ? 'var(--mantine-color-azul-0)' : undefined }}
              onClick={() => { setSelectedRun(null); setSelected(t.id); }}>
              {/* TL-WIDTH (Pedro 2026-10-25): these columns never wrap. */}
              <Table.Td style={NOWRAP}><Text size="sm" fw={600} span pl={inRun ? 24 : 0}>{inRun ? '↳ ' : ''}{t.serial}</Text></Table.Td>
              <Table.Td style={NOWRAP}><TradeStatusBadge t={t} /></Table.Td>
              <Table.Td style={NOWRAP}>{dateOf(t.created_at)}</Table.Td>
              <Table.Td style={NOWRAP}>{timeOf(t.created_at)}</Table.Td>
              <Table.Td style={NOWRAP}>{t.portfolio_label ?? t.portfolio}</Table.Td>
              {showAgreement && <Table.Td style={NOWRAP}>{t.agreement_label ?? <Text span size="sm" c="dimmed">Not linked</Text>}</Table.Td>}
              <Table.Td>{t.title ?? <Text span size="sm" c="dimmed">Untitled</Text>}</Table.Td>
              <Table.Td style={NOWRAP}><TradeFlowStrip t={t} /></Table.Td>
              <Table.Td style={{ textAlign: 'right' }}>{t.line_count}</Table.Td>
              <Table.Td style={{ textAlign: 'right' }}>{t.transfer_count ?? 0}</Table.Td>
              <Table.Td style={NOWRAP}>{t.created_by_name ?? '-'}</Table.Td>
              <Table.Td style={NOWRAP}>{t.updated_by_name ?? '-'} · {dateOf(t.updated_at)}</Table.Td>
              {/* N3 / N4: the documents stored at the last save. */}
              <Table.Td style={NOWRAP} onClick={(e) => e.stopPropagation()}>
                {t.documents_at ? (
                  <Tooltip withArrow label={`Made when saved: ${dateOf(t.documents_at)} ${timeOf(t.documents_at)}`}>
                    <Group gap={8} wrap="nowrap">
                      <Anchor size="sm" onClick={() => void open(t, 'pdf')}>PDF</Anchor>
                      <Anchor size="sm" onClick={() => void open(t, 'xlsx')}>Excel</Anchor>
                    </Group>
                  </Tooltip>
                ) : <Text span size="xs" c="dimmed">{inRun ? 'With the run’s documents (its row above)' : 'Save to make'}</Text>}
              </Table.Td>
            </Table.Tr>
    );
  }
}
